/**
 * The employee's warning letters, shown to HR before issuing a new one (owner, 2026-11-04):
 * how many active warnings, each with level, issue and violation dates, reason, reference, status
 * and deduction; cancelled or archived ones apart and not counted; the suggested next level
 * (HR still chooses) and a warning when the chosen level is at or below the last active one.
 * Also the optional one-time deduction (amount and/or days): it needs HR Manager approval and
 * never changes the basic salary. Backend: GET /api/warnings/employee/{id}/history.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const levelRank = (value) => {
  const v = String(value || '').toLowerCase();
  if (!v) return null;
  if (v.includes('final') || v.includes('نهائي')) return 3;
  if (v.includes('second') || v.includes('ثاني')) return 2;
  if (v.includes('first') || v.includes('أول') || v.includes('اول')) return 1;
  return null;
};

const STATUS = {
  active: ['Active', 'ساري'], cancelled: ['Cancelled', 'ملغى'], archived: ['Archived', 'مؤرشف'], deleted: ['Deleted', 'محذوف'],
};
const DED_STATUS = { pending: ['awaiting HR Manager', 'بانتظار مدير الموارد البشرية'], approved: ['approved', 'معتمد'], rejected: ['rejected', 'مرفوض'] };

function Row({ w, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const d = w.deduction;
  return (
    <li className="border-b last:border-0 py-1.5 text-xs" data-testid={`warning-history-${w.reference_no}`}>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        <b>{t(`${w.level.en} warning`, `إنذار ${w.level.ar}`)}</b>
        <span>{t('Issued', 'أُصدر')}: {w.issue_date || '—'}</span>
        <span>{t('Violation date', 'تاريخ المخالفة')}: {w.violation_date || '—'}</span>
        <a className="text-blue-700 underline" href={`/warnings/${encodeURIComponent(w.reference_no)}`} target="_blank" rel="noreferrer">{w.reference_no}</a>
        <span className={w.status === 'active' ? 'text-emerald-700' : 'text-slate-500'}>{t(...STATUS[w.status] || [w.status, w.status])}</span>
        {w.superseded && <span className="text-slate-400">{t('(earlier version of the document)', '(نسخة سابقة من المستند)')}</span>}
      </div>
      {w.reason && <div className="text-slate-600">{t('Reason', 'السبب')}: {w.reason}</div>}
      {w.cancellation_reason && w.status !== 'active' && <div className="text-slate-500">{w.cancellation_reason}</div>}
      {d && (
        <div className="text-rose-700">
          {t('Deduction', 'خصم')}: {d.amount != null ? `${d.amount} SAR` : (d.has_amount ? t('amount hidden', 'المبلغ مخفي') : '')}
          {d.days ? ` ${d.amount != null || d.has_amount ? '+' : ''} ${d.days} ${t('day(s)', 'يوم')}` : ''}
          {' · '}{t(...DED_STATUS[d.status] || [d.status, d.status])} · {t('pay period', 'فترة الراتب')} {d.pay_period}
        </div>
      )}
    </li>
  );
}

export default function WarningHistoryPanel({ employeeId, token, isRTL, levelOptions = [], selectedLevel, onPickLevel,
  values = {}, onChange, testid = 'warning-history' }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [h, setH] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!employeeId || !token) return;
    axios.get(`${API_URL}/warnings/employee/${employeeId}/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setH(r.data)).catch((e) => setError(e?.response?.data?.detail?.error || e.message));
  }, [employeeId, token]);

  if (error) return <p className="text-xs text-red-700">{error}</p>;
  if (!h) return <p className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{t('Loading warnings…', 'جارٍ تحميل الإنذارات…')}</p>;

  const suggestedOption = levelOptions.find((o) => levelRank(o.value) === h.suggested_level.rank);
  const chosen = levelRank(selectedLevel);
  const tooLow = chosen && h.warn_at_or_below && chosen <= h.warn_at_or_below;

  return (
    <div className="rounded border bg-slate-50 p-3 space-y-2 text-sm" data-testid={testid}>
      <div className="font-semibold" data-testid={`${testid}-count`}>
        {t(`Active warnings for this employee: ${h.active_count}`, `الإنذارات السارية لهذا الموظف: ${h.active_count}`)}
      </div>
      {h.active.length > 0 && <ul>{h.active.map((w) => <Row key={`${w.source}-${w.id}`} w={w} isRTL={isRTL} />)}</ul>}
      {h.cancelled.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500">{t(`Cancelled or archived (not counted): ${h.cancelled.length}`, `ملغاة أو مؤرشفة (لا تُحتسب): ${h.cancelled.length}`)}</summary>
          <ul>{h.cancelled.map((w) => <Row key={`${w.source}-${w.id}`} w={w} isRTL={isRTL} />)}</ul>
        </details>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>{t(`Suggested level: ${h.suggested_level.en}`, `المستوى المقترح: ${h.suggested_level.ar}`)}
          {h.last_active_level && t(` (last active: ${h.last_active_level.en})`, ` (آخر إنذار ساري: ${h.last_active_level.ar})`)}</span>
        {suggestedOption && onPickLevel && selectedLevel !== suggestedOption.value && (
          <button type="button" className="underline text-blue-700" onClick={() => onPickLevel(suggestedOption.value)} data-testid={`${testid}-use-suggested`}>
            {t('Use it', 'استخدمه')}</button>
        )}
      </div>
      {tooLow && (
        <p className="flex items-center gap-1 text-xs text-amber-800" role="alert" data-testid={`${testid}-level-warning`}>
          <AlertTriangle className="h-3.5 w-3.5" />
          {t(`The chosen level is at or below the last active warning (${h.last_active_level.en}). You can still issue it.`,
            `المستوى المختار مساوٍ لآخر إنذار ساري (${h.last_active_level.ar}) أو أقل منه. يمكنك الإصدار مع ذلك.`)}
        </p>
      )}
      {onChange && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t">
          <div className="col-span-2 text-xs text-slate-600">
            {t('One-time deduction (optional): needs HR Manager approval, applies to the pay period of the violation date, never changes the basic salary.',
              'خصم لمرة واحدة (اختياري): يحتاج اعتماد مدير الموارد البشرية، ويُطبق على فترة الراتب لتاريخ المخالفة، ولا يغيّر الراتب الأساسي.')}
          </div>
          <div>
            <Label className="text-xs">{t('Amount (SAR)', 'المبلغ (ريال)')}</Label>
            <Input type="number" min="0" step="0.01" value={values.deduction_from_salary ?? ''} data-testid={`${testid}-amount`}
              onChange={(e) => onChange('deduction_from_salary', e.target.value === '' ? undefined : e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">{t('Days', 'أيام')}</Label>
            <Input type="number" min="0" step="0.5" value={values.deduction_days ?? ''} data-testid={`${testid}-days`}
              onChange={(e) => onChange('deduction_days', e.target.value === '' ? undefined : e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}
